import { useEffect, useMemo, useState } from "react";
import type { ContentsItem } from "../contexts/WithPublication/PublicationContext";

const HEADING_TYPES = new Set(["h1", "h2", "h3"]);

export interface HeadingPortalTarget {
  sectionId: string;
  sectionLabel: string;
  container: HTMLDivElement;
}

const collectHeadings = (
  items: ContentsItem[],
): { id: string; label: string }[] => {
  const headings: { id: string; label: string }[] = [];
  const walk = (nodes: ContentsItem[]) => {
    for (const node of nodes) {
      if (HEADING_TYPES.has(node.type) && node.label) {
        headings.push({ id: node.id, label: node.label });
      }
      if (node.children) {
        walk(node.children);
      }
    }
  };
  walk(items);
  return headings;
};

export const useBookmarks = ({
  contents,
  containerRef,
  htmlChunks,
}: {
  contents?: ContentsItem[];
  containerRef: React.RefObject<HTMLDivElement | null>;
  htmlChunks?: Record<number, string>;
}) => {
  const [portalTargets, setPortalTargets] = useState<HeadingPortalTarget[]>([]);

  const headings = useMemo(
    () => (contents ? collectHeadings(contents) : []),
    [contents],
  );

  useEffect(() => {
    if (!containerRef.current || !htmlChunks) return;

    const targets: HeadingPortalTarget[] = [];
    const portalContainers: HTMLDivElement[] = [];

    for (const { id, label } of headings) {
      const element = document.getElementById(id);
      if (!element) continue;

      // Skip if we already injected a portal container
      const existing = element.querySelector("[data-bookmark-portal]");
      if (existing) continue;

      const portalDiv = document.createElement("div");
      portalDiv.setAttribute("data-bookmark-portal", id);
      portalDiv.style.cssText = `
        align-items: center; 
        bottom: 0; 
        display: flex; 
        justify-content: flex-end;
        left: -20px;
        padding-bottom: inherit;
        padding-top: inherit;
        position: absolute;
        top: 0;
        user-select: none;
        width: 0;
        z-index: 1;
      `;
      element.appendChild(portalDiv);
      portalContainers.push(portalDiv);

      targets.push({
        sectionId: id,
        sectionLabel: label,
        container: portalDiv,
      });
    }

    if (targets.length > 0) {
      setPortalTargets((prev) => [...prev, ...targets]);
    }

    return () => {
      for (const div of portalContainers) {
        div.remove();
      }
      if (portalContainers.length > 0) {
        setPortalTargets((prev) =>
          prev.filter((t) => !portalContainers.includes(t.container)),
        );
      }
    };
  }, [headings, containerRef, htmlChunks]);

  return { portalTargets };
};
